// График дашборда стенда мембран: сырые точки измерений + сглаженная
// аппроксимация + вертикальный крестик с тултипом.  Крестик синхронный:
// hoverX (время, ms epoch) живёт в родителе, все графики страницы рисуют
// линию и тултип на одном и том же времени — ведёшь мышью по температуре,
// значения подсвечиваются и на давлении, и на расходе.
//
// Тот же подход, что RigChart в MembraneSessions: лёгкий inline-SVG без
// сторонних библиотек, цвета — через CSS-переменные (тёмная тема бесплатно),
// null в ряду = разрыв линии, не интерполяция.

import { memo, useMemo, useRef } from "react";
import type { PointerEvent } from "react";

import { formatTime } from "@/lib/format";

/** Точка ряда: raw — измерение (бакет-среднее), smooth — аппроксимация.
 *  Для «одинарных» графиков (расход) raw = null, рисуется только линия. */
export interface DashChartPoint {
  x: number;
  raw: number | null;
  smooth: number | null;
}

const W = 900;
// Высота viewBox по умолчанию.  Ширина всегда 100% контейнера, высота
// подтягивается по пропорции viewBox — значит «выше график» = больше H.
const DEFAULT_H = 240;
const PAD = { top: 12, right: 14, bottom: 24, left: 44 };
const INNER_W = W - PAD.left - PAD.right;

/** Режем ряд на непрерывные сегменты: каждый null закрывает текущий. */
function segments(
  points: DashChartPoint[],
  pick: (p: DashChartPoint) => number | null,
): Array<Array<{ x: number; y: number }>> {
  const out: Array<Array<{ x: number; y: number }>> = [];
  let cur: Array<{ x: number; y: number }> = [];
  for (const p of points) {
    const y = pick(p);
    if (y === null || !Number.isFinite(y)) {
      if (cur.length > 0) {
        out.push(cur);
        cur = [];
      }
      continue;
    }
    cur.push({ x: p.x, y });
  }
  if (cur.length > 0) out.push(cur);
  return out;
}

interface Scales {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  sx: (x: number) => number;
  sy: (y: number) => number;
}

/** Статичное тело графика (сетка, точки, аппроксимация, ось времени).
 *  Вынесено в memo-компонент, чтобы движение крестика (hoverX меняется на
 *  каждый mousemove) не перерисовывало сотни кружков-измерений. */
const ChartBody = memo(function ChartBody({
  points,
  scales,
  color,
  showRaw,
  height,
}: {
  points: DashChartPoint[];
  scales: Scales;
  color: string;
  showRaw: boolean;
  height: number;
}) {
  const { minX, maxX, minY, maxY, sx, sy } = scales;
  const ticks = Array.from({ length: 5 }, (_, i) => minY + ((maxY - minY) * i) / 4);
  // Знаки подписей оси Y — от размаха: у давления в простое размах ~0.02 бар,
  // с фиксированным toFixed(1) все пять подписей схлопывались в «0.0».
  const range = maxY - minY;
  const tickDigits = range < 0.05 ? 3 : range < 0.5 ? 2 : range < 5 ? 1 : 0;
  const smoothSegs = segments(points, (p) => p.smooth);
  const rawPts = showRaw
    ? points.filter((p): p is DashChartPoint & { raw: number } => p.raw !== null && Number.isFinite(p.raw))
    : [];
  // Подписи оси времени: 4 равномерные метки, как в макете.
  const timeTicks = Array.from({ length: 4 }, (_, i) => minX + ((maxX - minX) * i) / 3);

  return (
    <g>
      {ticks.map((tv, i) => {
        const y = sy(tv);
        return (
          <g key={i}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y} y2={y} stroke="var(--color-border)" strokeWidth={1} />
            <text
              x={PAD.left - 8}
              y={y + 3}
              textAnchor="end"
              fontSize={10}
              fontFamily="var(--font-mono)"
              fill="var(--color-text-muted)"
            >
              {tv.toFixed(tickDigits)}
            </text>
          </g>
        );
      })}

      {rawPts.map((p, i) => (
        <circle key={`r-${i}`} cx={sx(p.x)} cy={sy(p.raw)} r={2} fill={color} opacity={0.4} />
      ))}

      {smoothSegs.map((seg, i) => (
        <path
          key={`s-${i}`}
          d={seg.map((p, j) => `${j === 0 ? "M" : "L"}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`).join(" ")}
          fill="none"
          stroke={color}
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ))}
      {/* Сегмент из одной точки путём не рисуется — подсвечиваем кружком. */}
      {smoothSegs
        .filter((seg) => seg.length === 1)
        .map((seg, i) => (
          <circle key={`solo-${i}`} cx={sx(seg[0].x)} cy={sy(seg[0].y)} r={2.5} fill={color} />
        ))}

      {timeTicks.map((tx, i) => (
        <text
          key={`t-${i}`}
          x={i === 0 ? PAD.left : i === 3 ? W - PAD.right : sx(tx)}
          y={height - 8}
          textAnchor={i === 0 ? "start" : i === 3 ? "end" : "middle"}
          fontSize={10}
          fontFamily="var(--font-mono)"
          fill="var(--color-text-muted)"
        >
          {formatTime(new Date(tx).toISOString())}
        </text>
      ))}
    </g>
  );
});

export function RigDashboardChart({
  title,
  unit,
  color,
  points,
  hoverX,
  onHover,
  currentText,
  legendRaw,
  legendSmooth,
  emptyText,
  valueDigits = 2,
  height = DEFAULT_H,
}: {
  title: string;
  unit: string;
  /** CSS-значение цвета серии, например "var(--color-chart-pressure)". */
  color: string;
  points: DashChartPoint[];
  /** Синхронное время крестика (ms epoch) или null — общее на все графики. */
  hoverX: number | null;
  onHover: (x: number | null) => void;
  /** Текущее значение в шапке карточки (уже отформатированное). */
  currentText: string;
  /** Подписи легенды; если legendRaw не задан — точки измерений не рисуются. */
  legendRaw?: string;
  legendSmooth?: string;
  emptyText: string;
  valueDigits?: number;
  /** Высота viewBox: чем больше, тем выше график на экране (ширина всегда
   *  100% контейнера).  По умолчанию DEFAULT_H. */
  height?: number;
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const innerH = height - PAD.top - PAD.bottom;

  const valid = points.filter((p) => (p.raw ?? p.smooth) !== null);

  const scales: Scales | null = useMemo(() => {
    if (points.length === 0 || valid.length === 0) return null;
    const xs = points.map((p) => p.x);
    const ys: number[] = [];
    for (const p of points) {
      if (p.raw !== null && Number.isFinite(p.raw)) ys.push(p.raw);
      if (p.smooth !== null && Number.isFinite(p.smooth)) ys.push(p.smooth);
    }
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    let minY = Math.min(...ys);
    let maxY = Math.max(...ys);
    if (minY === maxY) {
      minY -= 1;
      maxY += 1;
    } else {
      const m = (maxY - minY) * 0.1;
      minY -= m;
      maxY += m;
    }
    const sx = (x: number) =>
      PAD.left + (maxX === minX ? INNER_W / 2 : ((x - minX) / (maxX - minX)) * INNER_W);
    const sy = (y: number) => PAD.top + innerH - ((y - minY) / (maxY - minY)) * innerH;
    return { minX, maxX, minY, maxY, sx, sy };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, innerH]);

  // Ближайшая к крестику точка с данными: и линия, и тултип снапятся к ней.
  // Бакеты у всех графиков страницы общие (один запрос ряда), поэтому снап
  // на всех графиках попадает в одно и то же время.
  const near = useMemo(() => {
    if (hoverX === null || scales === null) return null;
    let best: DashChartPoint | null = null;
    let bestD = Infinity;
    for (const p of points) {
      if ((p.smooth ?? p.raw) === null) continue;
      const d = Math.abs(p.x - hoverX);
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    return best;
  }, [hoverX, points, scales]);

  const handleMove = (e: PointerEvent<SVGSVGElement>) => {
    const el = svgRef.current;
    if (!el || scales === null) return;
    const rect = el.getBoundingClientRect();
    const frac = (e.clientX - rect.left) / rect.width;
    const vx = frac * W;
    if (vx < PAD.left || vx > W - PAD.right) {
      onHover(null);
      return;
    }
    onHover(scales.minX + ((vx - PAD.left) / INNER_W) * (scales.maxX - scales.minX));
  };

  const nearValue = near === null ? null : (near.smooth ?? near.raw);
  const nearX = near === null || scales === null ? null : scales.sx(near.x);
  // Тултип уводим влево от линии в правой трети графика, чтобы не резался.
  const tipLeftPct = nearX === null ? 0 : (nearX / W) * 100;
  const tipFlip = tipLeftPct > 62;

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
          <span className="text-sm font-medium text-text-secondary truncate">{title}</span>
          {legendRaw && legendSmooth && (
            <span className="hidden sm:flex items-center gap-3 pl-2 text-xs text-text-muted">
              <span className="flex items-center gap-1.5">
                <svg width="16" height="4" aria-hidden>
                  <circle cx="2" cy="2" r="1.6" fill={color} opacity={0.4} />
                  <circle cx="8" cy="2" r="1.6" fill={color} opacity={0.4} />
                  <circle cx="14" cy="2" r="1.6" fill={color} opacity={0.4} />
                </svg>
                {legendRaw}
              </span>
              <span className="flex items-center gap-1.5">
                <svg width="16" height="4" aria-hidden>
                  <rect x="0" y="1" width="16" height="2.5" rx="1.25" fill={color} />
                </svg>
                {legendSmooth}
              </span>
            </span>
          )}
        </div>
        <span className="text-base font-semibold text-text-primary tabular-nums">{currentText}</span>
      </div>

      {scales === null ? (
        <p className="text-text-muted text-sm py-10 text-center">{emptyText}</p>
      ) : (
        <div className="relative mt-2">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${W} ${height}`}
            className="w-full h-auto touch-none"
            role="img"
            onPointerMove={handleMove}
            onPointerDown={handleMove}
            onPointerLeave={() => onHover(null)}
          >
            <ChartBody
              points={points}
              scales={scales}
              color={color}
              showRaw={Boolean(legendRaw)}
              height={height}
            />
            {nearX !== null && nearValue !== null && (
              <g pointerEvents="none">
                <line
                  x1={nearX}
                  x2={nearX}
                  y1={PAD.top}
                  y2={PAD.top + innerH}
                  stroke="var(--color-text-muted)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
                <circle
                  cx={nearX}
                  cy={scales.sy(nearValue)}
                  r={4.5}
                  fill={color}
                  stroke="var(--color-bg-surface)"
                  strokeWidth={2}
                />
              </g>
            )}
          </svg>
          {near !== null && nearValue !== null && (
            <div
              className="card absolute top-1 px-2.5 py-1.5 pointer-events-none shadow-md whitespace-nowrap"
              style={
                tipFlip
                  ? { right: `${100 - tipLeftPct}%`, marginRight: 10 }
                  : { left: `${tipLeftPct}%`, marginLeft: 10 }
              }
            >
              <div className="text-[10px] leading-tight text-text-muted font-mono">
                {formatTime(new Date(near.x).toISOString())}
              </div>
              <div className="flex items-center gap-1.5 text-[13px] font-semibold text-text-primary tabular-nums">
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                {nearValue.toFixed(valueDigits)} {unit}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
